import { useState } from "react"

import useHasAccess from "@hooks/useHasAccess"

import { NO_REFERENCE_SENTINEL } from "@utils/catalogSubscriptions"
import { faIcon } from "@utils/faIcon"
import replaceSlugs from "@utils/replaceSlugs"

import { BULLET_CHARACTER } from "@particles/Bullet/Bullet"

import { Avatar, IconButton } from "@atoms/index"

import AlertBadge from "@molecules/AlertBadge/AlertBadge"
import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"
import { Prompt } from "@molecules/index"

import { MenuContentNode } from "@organisms/Menu/Menu.types"
import { Menu } from "@organisms/index"

import { CATALOG_ROUTES } from "@routes/catalogs"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./CatalogLinkSection.module.scss"
import {
    useCatalogLinkDisconnectMutation,
    useCatalogLinkReconnectMutation,
    useCatalogLinksQuery,
    useInvalidateCatalogLinks,
} from "./CatalogLinkSection.store"
import { CatalogLinkItemProps, CatalogLinkSectionProps } from "./CatalogLinkSection.types"

function CatalogLinkItem(props: CatalogLinkItemProps) {
    const { link, onLinkChanged } = props

    const { hasPermission } = useHasAccess()
    // The catalog item page sits under Settings, so viewing it takes the settings page on top of the pricebook view.
    const hasCatalogItemViewPermission =
        hasPermission("settings_view_permission", PERMISSION_LEVEL.RESTRICTED) &&
        hasPermission("pricebook_view_permission", PERMISSION_LEVEL.FULL)
    const hasPricebookEditPermission = hasPermission("pricebook_edit_permission", PERMISSION_LEVEL.FULL)

    const disconnectMutation = useCatalogLinkDisconnectMutation()
    const reconnectMutation = useCatalogLinkReconnectMutation()

    const [isDisconnectPromptOpen, setIsDisconnectPromptOpen] = useState(false)

    const catalogItemRoute = replaceSlugs(CATALOG_ROUTES.ITEM_DETAILS, {
        catalog_id: String(link.catalog.id),
        brand_id: String(link.catalog_item.brand ?? NO_REFERENCE_SENTINEL),
        item_id: String(link.catalog_item.id),
    })
    const isActionPending = disconnectMutation.isPending || reconnectMutation.isPending

    const handleDisconnectConfirm = () => {
        setIsDisconnectPromptOpen(false)
        disconnectMutation.mutate(link, { onSuccess: onLinkChanged })
    }

    const menuItems: MenuContentNode[] = [
        {
            type: "group",
            items: [
                ...(hasCatalogItemViewPermission
                    ? [
                          {
                              title: "View catalog item",
                              icon: faIcon({ name: "arrow-up-right-from-square", style: "regular" as const }),
                              path: catalogItemRoute,
                          },
                      ]
                    : []),
                ...(hasPricebookEditPermission && !link.is_connected
                    ? [
                          {
                              title: "Reconnect",
                              icon: faIcon({ name: "arrows-rotate", style: "regular" as const }),
                              onClick: () => reconnectMutation.mutate(link, { onSuccess: onLinkChanged }),
                          },
                      ]
                    : []),
            ],
        },
        ...(hasPricebookEditPermission && link.is_connected
            ? [
                  { type: "divider" as const },
                  {
                      type: "group" as const,
                      items: [
                          {
                              variant: "danger" as const,
                              title: "Disconnect",
                              icon: faIcon({ name: "ban", style: "regular" as const }),
                              onClick: () => setIsDisconnectPromptOpen(true),
                          },
                      ],
                  },
              ]
            : []),
    ]

    return (
        <>
            <ListItem.Root>
                <ListItem.AvatarAndContent
                    avatar={<Avatar variant="company" size="lg" fallbackName={link.catalog_item.name} />}
                    content={
                        <ListItemContent
                            leftLabel={
                                <ListItem.Label
                                    label={link.catalog_item.name}
                                    suffixSlot={
                                        <AlertBadge
                                            status={link.is_connected ? "success" : "neutral"}
                                            size="sm"
                                            showIcon={true}
                                            isLoading={false}
                                        >
                                            {link.is_connected ? "Connected" : "Disconnected"}
                                        </AlertBadge>
                                    }
                                />
                            }
                            leftCaption={
                                <ListItem.Caption
                                    caption={`${link.catalog.name}${link.catalog_item.sku ? ` ${BULLET_CHARACTER} SKU ${link.catalog_item.sku}` : ""}`}
                                />
                            }
                        />
                    }
                />
                {(hasCatalogItemViewPermission || hasPricebookEditPermission) && (
                    <ListItem.Actions>
                        <Menu
                            popoverSide="bottom"
                            popoverAlign="end"
                            title={link.catalog_item.name}
                            trigger={
                                <IconButton
                                    variant="ghost"
                                    size="md"
                                    icon={faIcon({ name: "ellipsis", style: "regular" })}
                                    aria-label="More options"
                                    isProcessing={isActionPending}
                                    isDisabled={isActionPending}
                                />
                            }
                            items={menuItems}
                        />
                    </ListItem.Actions>
                )}
            </ListItem.Root>

            <Prompt
                title={`Disconnect from ${link.catalog_item.name}?`}
                body="This pricebook item will no longer receive catalog updates like price changes. You can reconnect it later from this page."
                isOpen={isDisconnectPromptOpen}
                confirmText="Disconnect"
                onConfirm={handleDisconnectConfirm}
                cancelText="Cancel"
                onCancel={() => setIsDisconnectPromptOpen(false)}
                isDangerousOperation={true}
            />
        </>
    )
}

export default function CatalogLinkSection(props: CatalogLinkSectionProps) {
    const { pricebookItemId } = props

    const linksQuery = useCatalogLinksQuery(pricebookItemId)
    const invalidateLinks = useInvalidateCatalogLinks(pricebookItemId)

    const links = linksQuery.data ?? []

    if (links.length === 0) {
        return null
    }

    return (
        <div className={styles.links}>
            {links.map((link) => (
                <CatalogLinkItem key={link.id} link={link} onLinkChanged={() => void invalidateLinks()} />
            ))}
        </div>
    )
}
